import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SITEMAP, FLOWS } from './sitemapData';
import './Sitemap.css';

/*
 * Sitemap & Documentation
 * ---------------------------------------------------------------------------
 * A purely additive, documentation-only module. It renders:
 *   1. A hierarchical System Sitemap of every module / submodule.
 *   2. Interactive flowcharts for the major business processes.
 *
 * It performs NO API calls, mutations or calculations. Nodes that map to a
 * real module deep-link into it by dispatching the existing global `navigate`
 * custom event (already handled in App.jsx) — no props/business logic touched.
 * ---------------------------------------------------------------------------
 */

const goTo = (page) => {
  if (!page) return;
  window.dispatchEvent(new CustomEvent('navigate', { detail: { page } }));
};

// ── Sitemap tree ────────────────────────────────────────────────────────────
function SitemapNode({ node }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <li className="sm-node">
      <div className="sm-node-row">
        {hasChildren ? (
          <button
            className="sm-toggle"
            onClick={() => setOpen(o => !o)}
            aria-label={open ? t('sitemap.collapse') : t('sitemap.expand')}
          >
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span className="sm-toggle sm-leaf">•</span>
        )}

        <button
          className={`sm-label ${node.page ? 'sm-linkable' : ''}`}
          onClick={() => goTo(node.page)}
          disabled={!node.page}
          title={node.page ? t('sitemap.openNode', { name: t(node.nameKey) }) : t(node.nameKey)}
        >
          {node.icon && <span className="sm-icon">{node.icon}</span>}
          <span className="sm-name">{t(node.nameKey)}</span>
          {node.page && <span className="sm-open-hint">↗</span>}
        </button>

        {node.descKey && <span className="sm-desc">{t(node.descKey)}</span>}
      </div>

      {hasChildren && open && (
        <ul className="sm-children">
          {node.children.map(child => (
            <SitemapNode key={child.id} node={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

function SitemapView() {
  const { t } = useTranslation();
  return (
    <div className="sm-tree-wrap">
      <p className="sm-hint">
        {t('sitemap.hintBefore')} <span className="sm-open-hint">↗</span> {t('sitemap.hintAfter')}
      </p>
      <ul className="sm-tree">
        {SITEMAP.map(node => (
          <SitemapNode key={node.id} node={node} />
        ))}
      </ul>
    </div>
  );
}

// ── Flowcharts ────────────────────────────────────────────────────────────--
const TYPE_META = {
  start:    { cls: 'fc-start',    badgeKey: 'sitemap.badges.start' },
  process:  { cls: 'fc-process',  badgeKey: 'sitemap.badges.step' },
  decision: { cls: 'fc-decision', badgeKey: 'sitemap.badges.decision' },
  end:      { cls: 'fc-end',      badgeKey: 'sitemap.badges.end' },
};

function FlowStep({ step, index, expanded, onToggle, isLast }) {
  const { t } = useTranslation();
  const meta = TYPE_META[step.type] || TYPE_META.process;
  return (
    <div className="fc-step">
      <div
        className={`fc-card ${meta.cls} ${expanded ? 'expanded' : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <div className="fc-card-head">
          <span className="fc-num">{index + 1}</span>
          <span className="fc-badge">{t(meta.badgeKey)}</span>
          <span className="fc-title">{t(step.labelKey)}</span>
          {step.detailKey && <span className="fc-caret">{expanded ? '−' : '+'}</span>}
        </div>
        {expanded && step.detailKey && (
          <div className="fc-detail">
            <p>{t(step.detailKey)}</p>
            {step.page && (
              <button
                className="fc-go-btn"
                onClick={(e) => { e.stopPropagation(); goTo(step.page); }}
              >
                {t('sitemap.openModule')} ↗
              </button>
            )}
          </div>
        )}
      </div>
      {!isLast && <div className="fc-connector"><span className="fc-arrow">▼</span></div>}
    </div>
  );
}

function FlowChart({ flow }) {
  const { t } = useTranslation();
  // All steps expanded by default so the printed/exported view is complete.
  const [expanded, setExpanded] = useState(() => flow.steps.map(() => true));

  const toggle = (i) =>
    setExpanded(prev => prev.map((v, idx) => (idx === i ? !v : v)));

  return (
    <div className="fc-flow">
      <div className="fc-flow-head">
        <h3>{flow.icon} {t(flow.titleKey)}</h3>
        <p>{t(flow.descKey)}</p>
      </div>
      <div className="fc-steps">
        {flow.steps.map((step, i) => (
          <FlowStep
            key={i}
            step={step}
            index={i}
            expanded={expanded[i]}
            onToggle={() => toggle(i)}
            isLast={i === flow.steps.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

function FlowchartsView() {
  const { t } = useTranslation();
  const [activeFlowId, setActiveFlowId] = useState(FLOWS[0].id);
  const activeFlow = FLOWS.find(f => f.id === activeFlowId) || FLOWS[0];

  return (
    <div className="fc-wrap">
      <div className="fc-flow-tabs no-print">
        {FLOWS.map(f => (
          <button
            key={f.id}
            className={`fc-flow-tab ${f.id === activeFlowId ? 'active' : ''}`}
            onClick={() => setActiveFlowId(f.id)}
          >
            <span>{f.icon}</span> {t(f.titleKey)}
          </button>
        ))}
      </div>

      {/* Active flow on screen */}
      <div className="fc-active no-print">
        <FlowChart flow={activeFlow} />
      </div>

      {/* When printing, render every flow so the full documentation is exported. */}
      <div className="fc-print-only">
        {FLOWS.map(f => <FlowChart key={f.id} flow={f} />)}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────--
export default function Sitemap() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('sitemap'); // 'sitemap' | 'flowcharts'

  const handlePrint = () => {
    document.body.classList.add('sitemap-printing');
    window.print();
    // Cleanup after the print dialog returns.
    window.setTimeout(() => document.body.classList.remove('sitemap-printing'), 500);
  };

  return (
    <div className="sitemap-page">
      <div className="sitemap-header no-print">
        <div>
          <h1>🗺️ {t('sitemap.pageTitle')}</h1>
          <p className="sitemap-sub">
            {t('sitemap.pageSub')}
          </p>
        </div>
        <button className="sitemap-print-btn" onClick={handlePrint}>
          🖨️ {t('sitemap.printExport')}
        </button>
      </div>

      <div className="sitemap-tabs no-print">
        <button
          className={`sitemap-tab ${tab === 'sitemap' ? 'active' : ''}`}
          onClick={() => setTab('sitemap')}
        >
          🧭 {t('sitemap.tabSitemap')}
        </button>
        <button
          className={`sitemap-tab ${tab === 'flowcharts' ? 'active' : ''}`}
          onClick={() => setTab('flowcharts')}
        >
          🔀 {t('sitemap.tabFlowcharts')}
        </button>
      </div>

      <div className="sitemap-body">
        {/* Print title (only visible on paper). */}
        <h1 className="print-only print-title">{t('sitemap.printTitle')}</h1>

        {tab === 'sitemap' ? <SitemapView /> : <FlowchartsView />}

        {/* When printing the Sitemap tab, also include the sitemap on paper. */}
      </div>
    </div>
  );
}
